import React, {useEffect, useState} from "react";
import {
	Box,
	Button,
	Pagination,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import {EmployeeRestControllerApiFactory, PageDtoPayrollList, PayrollList} from "arteco-api-client-ts";
import dayjs from "dayjs";
import {useApiManager} from "../../../components/utils/api";
import {numberToString} from "../../../components/utils/numbers";
import {fileLocalSave} from "../../../components/form/form_inputs/form_file";
import {
	btnSx,
	EmptyState,
	ListCard,
	Page,
	PageHeader,
	Panel,
	SkeletonRows,
	tableContainerSx,
	tableSx,
} from "../../../components/kit";

const HIDDEN = "•••••";

/** «Septiembre 2026» si el periodo es un mes natural; si no, «01/09/2026 – 15/09/2026». */
const periodLabel = (p: PayrollList) => {
	const from = dayjs(p.periodFrom);
	const to = dayjs(p.periodTo);
	if (from.date() === 1 && to.isSame(from.endOf("month"), "day")) {
		const s = from.format("MMMM YYYY");
		return s.charAt(0).toUpperCase() + s.substring(1);
	}
	return `${from.format("DD/MM/YYYY")} – ${to.format("DD/MM/YYYY")}`;
};

/**
 * Nóminas del empleado: una fila por periodo con el líquido y el coste de empresa, ocultos hasta que
 * se pide verlos; al pulsar la fila se descarga el PDF.
 */
export const EmployeePayrollPage = () => {
	const apiManager = useApiManager();
	const employeeApi = apiManager.factory(EmployeeRestControllerApiFactory);
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const [showSensible, setShowSensible] = useState<boolean>(false);
	const [page, setPage] = useState(1);
	const [result, setResult] = useState<PageDtoPayrollList>();

	useEffect(() => {
		setResult(undefined);
		apiManager.execute(employeeApi.getEmployeePayrolls(page - 1, undefined, undefined))
			.then(setResult)
			.catch(() => setResult({content: [], totalPages: 0, totalElements: 0}));
	}, [page]);

	const download = (p: PayrollList) => {
		if (!p.id) throw new Error("No payroll id");
		employeeApi.viewEmployeePayroll(p.id).then((resp) => fileLocalSave(resp));
	};

	const amount = (n?: number) => showSensible ? `${numberToString(n)} €` : HIDDEN;
	const rows = result?.content || [];

	return (
		<Page>
			<PageHeader title="Nóminas"
						subtitle="Pulsa en una nómina para descargar el PDF."
						actions={
							<Button variant="outlined" onClick={() => setShowSensible(!showSensible)} sx={btnSx}
									startIcon={showSensible ? <VisibilityOffRoundedIcon/> : <VisibilityRoundedIcon/>}>
								{showSensible ? "Ocultar importes" : "Mostrar importes"}
							</Button>
						}/>

			<Panel>
				{!result ? <SkeletonRows rows={6}/> : rows.length === 0 ? (
					<EmptyState icon={<ReceiptLongRoundedIcon/>} title="Aún no hay nóminas"
								description="Cuando RR. HH. suba la de este mes, la verás aquí."/>
				) : compact ? (
					<Stack spacing={1.5}>
						{rows.map(p => (
							<ListCard key={p.id} onClick={() => download(p)}>
								<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
									<Typography variant="body2" fontWeight={700}>{periodLabel(p)}</Typography>
									<FileDownloadOutlinedIcon fontSize="small" color="action"/>
								</Stack>
								<Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1}}>
									<Box>
										<Typography variant="caption" color="text.secondary" display="block">Líquido</Typography>
										<Typography variant="body2" fontWeight={600} sx={{fontVariantNumeric: "tabular-nums"}}>{amount(p.totalLiquido)}</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary" display="block">Coste empresa</Typography>
										<Typography variant="body2" fontWeight={600} sx={{fontVariantNumeric: "tabular-nums"}}>{amount(p.costeEmpresa)}</Typography>
									</Box>
								</Box>
							</ListCard>
						))}
					</Stack>
				) : (
					<TableContainer sx={tableContainerSx}>
						<Table size="small" sx={tableSx}>
							<TableHead>
								<TableRow>
									<TableCell>Periodo</TableCell>
									<TableCell align="right">Líquido</TableCell>
									<TableCell align="right">Coste empresa</TableCell>
									<TableCell sx={{width: 56}}/>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map(p => (
									<TableRow key={p.id} hover onClick={() => download(p)} sx={{cursor: "pointer"}}>
										<TableCell>
											<Typography variant="body2" fontWeight={600}>{periodLabel(p)}</Typography>
										</TableCell>
										<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{amount(p.totalLiquido)}</TableCell>
										<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{amount(p.costeEmpresa)}</TableCell>
										<TableCell align="right" sx={{color: "text.secondary"}}>
											<Tooltip title="Descargar PDF"><FileDownloadOutlinedIcon fontSize="small"/></Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{(result?.totalPages || 0) > 1 && (
					<Stack alignItems="center" sx={{mt: 3}}>
						<Pagination count={result?.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded"
									size={compact ? "small" : "medium"}/>
					</Stack>
				)}
			</Panel>
		</Page>
	);
};
